import * as geojson from 'geojson';

import {ApiFeature, Site, SiteType} from './api/project';

type SiteFeatureIdKey = 'drainageAreaFeatureId' | 'forecastPointFeatureId';

type SourceFeatures = {
  features: geojson.Feature[];
  // each site's feature id in this source (fetched or synthetic), for selection feature state
  featureIdBySiteId: {[siteId: Site['id']]: number};
};

// populate the feature with the site info so that we can style point/basin
// layers by site type and resolve map clicks back to a site
export function annotateFeaturesWithSite(
  features: geojson.Feature[],
  sites: Site[],
  featureIdKey: SiteFeatureIdKey
): geojson.Feature[] {
  const siteByFeatureId = new Map<geojson.Feature['id'], Site>();
  for (const site of sites) {
    const featureId = site.featureIds[featureIdKey];
    if (featureId !== undefined) {
      siteByFeatureId.set(featureId, site);
    }
  }
  return features.map((f) => {
    const site = siteByFeatureId.get(f.id);
    return {
      ...f,
      properties: {
        ...f.properties,
        ...(site && {siteId: site.id, siteName: site.name, siteType: site.siteType}),
      },
    };
  });
}

function isPolygonal(geometry: geojson.Geometry): boolean {
  return geometry.type === 'Polygon' || geometry.type === 'MultiPolygon';
}

/**
 * Sites on migrated (H5) projects have no DB features to fetch by featureIds;
 * their geometry comes inline on the site instead. Build map features for
 * those directly, routed by geometry type: polygons go to the drainage source
 * (rendered by the basin fill/stroke layers, so they default to the basin site
 * type), points to the forecast-point source (circle layers, location site
 * type). Mapbox feature state needs numeric feature ids, so the built features
 * get synthetic ids above any fetched feature id in their source. Each source's
 * featureIdBySiteId maps every site to its feature id, fetched or synthetic.
 */
export function makeSiteMapFeatures(
  sites: Site[],
  fetchedDrainageFeatures: ApiFeature[],
  fetchedForecastPointFeatures: ApiFeature[]
): {drainage: SourceFeatures; forecastPoints: SourceFeatures} {
  const drainage: SourceFeatures = {
    features: annotateFeaturesWithSite(fetchedDrainageFeatures, sites, 'drainageAreaFeatureId'),
    featureIdBySiteId: {},
  };
  const forecastPoints: SourceFeatures = {
    features: annotateFeaturesWithSite(
      fetchedForecastPointFeatures,
      sites,
      'forecastPointFeatureId'
    ),
    featureIdBySiteId: {},
  };
  let nextDrainageId = Math.max(0, ...fetchedDrainageFeatures.map((f) => f.id)) + 1;
  let nextForecastPointId = Math.max(0, ...fetchedForecastPointFeatures.map((f) => f.id)) + 1;

  for (const site of sites) {
    const {drainageAreaFeatureId, forecastPointFeatureId} = site.featureIds;
    if (drainageAreaFeatureId !== undefined) {
      drainage.featureIdBySiteId[site.id] = drainageAreaFeatureId;
    } else if (site.geometry && isPolygonal(site.geometry)) {
      pushInlineGeometryFeature(drainage, site, nextDrainageId++, SiteType.BASIN);
    }
    if (forecastPointFeatureId !== undefined) {
      forecastPoints.featureIdBySiteId[site.id] = forecastPointFeatureId;
    } else if (site.geometry && !isPolygonal(site.geometry)) {
      pushInlineGeometryFeature(forecastPoints, site, nextForecastPointId++, SiteType.LOCATION);
    }
  }
  return {drainage, forecastPoints};
}

function pushInlineGeometryFeature(
  source: SourceFeatures,
  site: Site,
  featureId: number,
  defaultSiteType: SiteType
) {
  source.featureIdBySiteId[site.id] = featureId;
  source.features.push({
    type: 'Feature',
    id: featureId,
    geometry: site.geometry!,
    properties: {
      siteId: site.id,
      siteName: site.name,
      siteType: site.siteType ?? defaultSiteType,
    },
  });
}
